import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Stack, TextField, Alert, Link, Box, Button } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { useAuth } from "../libs/AuthContext";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const schema = Yup.object({
  Email: Yup.string()
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      "Invalid email address"
    ),
  Password: Yup.string().required("Password is required")
});

export default function Login() {
  const { userHasAuthenticated, emailAddressHandler } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const handleSubmit = async (event) => {
    setIsLoading(true);
    setAlertMessage("");
    setInfoMessage("");
    try {
      if (isNewPasswordRequired) {
        await confirmSignIn({ challengeResponse: event.NewPassword });
        setIsNewPasswordRequired(false);
        setIsLoading(false);
        userHasAuthenticated(true);
        return;
      }

      const result = await signIn({ username: event.Email, password: event.Password });
      if (result?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setIsNewPasswordRequired(true);
        setIsLoading(false);
        setInfoMessage("Please change your password to continue.");
      } else if (result?.isSignedIn) {
        userHasAuthenticated(true);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      if (e?.name === "UserNotConfirmedException") {
        emailAddressHandler(event.Email);
        navigate("/login/verify");
        return;
      }
      setAlertMessage(e?.message ?? "Login failed");
      setIsLoading(false);
    }
  };

  return (
    <FormPage title="Sign in to DataStar" subtitle="Manage and access your DataStar license keys.">
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      {infoMessage ? (
        <Alert severity="info" onClose={() => setInfoMessage("")}>{infoMessage}</Alert>
      ) : null}
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{ Email: "", Password: "", NewPassword: "", ConfirmPassword: "" }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                name="Email"
                label="Email"
                type="email"
                value={values.Email}
                onChange={handleChange}
                error={!!errors.Email}
                helperText={errors.Email}
                autoComplete="email"
                autoFocus
              />
              {!isNewPasswordRequired ? (
                <TextField
                  name="Password"
                  label="Password"
                  type="password"
                  value={values.Password}
                  onChange={handleChange}
                  error={!!errors.Password}
                  helperText={errors.Password}
                  autoComplete="current-password"
                />
              ) : (
                <>
                  <TextField
                    name="NewPassword"
                    label="New Password"
                    type="password"
                    value={values.NewPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <TextField
                    name="ConfirmPassword"
                    label="Confirm New Password"
                    type="password"
                    value={values.ConfirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </>
              )}
              {!isNewPasswordRequired ? (
                <Box sx={{ textAlign: "right", mt: -1 }}>
                  <Link component={RouterLink} to="/login/reset" variant="body2">
                    Forgot password?
                  </Link>
                </Box>
              ) : null}
              <Stack direction="row" spacing={1.5}>
                <LoaderButton
                  type="submit"
                  isLoading={isLoading}
                  text={isNewPasswordRequired ? "Update Password" : "Sign In"}
                  loadingText={isNewPasswordRequired ? "Updating…" : "Signing in…"}
                  fullWidth
                />
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="outlined"
                  fullWidth
                  disabled={isLoading}
                >
                  Register
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Formik>
    </FormPage>
  );
}
